import type {
    ProviderAIPhone,
    ProviderDocument,
    ProviderDocumentTranscription,
    ProviderLetter,
    ProviderMail,
    ProviderPhone,
    ProviderReceiveFax,
    ProviderSendEmail,
    ProviderSendFax,
    SupportAIPhone,
    SupportDocument,
    SupportDocumentTranscription,
    SupportLetter,
    SupportMail,
    SupportPhone,
    SupportReceiveFax,
    SupportSendEmail,
    SupportSendFax,
} from '../../api';

/**
 * The File Cabinet (FE-225): every document the account holds, and the mail, faxes, emails and
 * calls that carried them — one core screen for both apps.
 *
 * Records are the generated models as the API returns them, provider or support. Each support model
 * carries every field of its provider twin plus support-only ones (tags, assignment), so the screen
 * reads the shared fields and the drawer can hand the record to the app unchanged.
 */

export type FileCabinetApp = 'provider' | 'support';

/** The tabs. `docs` is the library every other channel's records point into. */
export type FileCabinetChannel = 'docs' | 'mail' | 'fax' | 'email' | 'call';

/** `other` is every call method that is neither inbound nor outbound; `ai` is the AI caller's own list. */
export type FileCabinetDirection = 'in' | 'out' | 'other' | 'ai';

/** One table: a channel, and for every channel but `docs` a direction. Each is one list endpoint. */
export type FileCabinetViewKey =
    | 'docs'
    | 'mail.in'
    | 'mail.out'
    | 'fax.in'
    | 'fax.out'
    | 'email.out'
    | 'call.in'
    | 'call.out'
    | 'call.other'
    | 'call.ai';

export type CabinetDocument = ProviderDocument | SupportDocument;
/** Received mail — `/mail`. */
export type CabinetMail = ProviderMail | SupportMail;
/** Outbound mail — `/letter`. */
export type CabinetLetter = ProviderLetter | SupportLetter;
export type CabinetReceivedFax = ProviderReceiveFax | SupportReceiveFax;
export type CabinetSentFax = ProviderSendFax | SupportSendFax;
export type CabinetEmail = ProviderSendEmail | SupportSendEmail;
/** A recorded call — `/phone`, every method. */
export type CabinetCall = ProviderPhone | SupportPhone;
/**
 * A call placed by the AI caller — `/phone/ai`. It has no `id` (its key is `aiPhoneId`), no claims, no
 * document and no transcript in the API (verified 2026-10-22).
 */
export type CabinetAiCall = ProviderAIPhone | SupportAIPhone;
export type CabinetTranscription = ProviderDocumentTranscription | SupportDocumentTranscription;

export interface FileCabinetRecordByView {
    docs: CabinetDocument;
    'mail.in': CabinetMail;
    'mail.out': CabinetLetter;
    'fax.in': CabinetReceivedFax;
    'fax.out': CabinetSentFax;
    'email.out': CabinetEmail;
    'call.in': CabinetCall;
    'call.out': CabinetCall;
    'call.other': CabinetCall;
    'call.ai': CabinetAiCall;
}

export type FileCabinetRecord = FileCabinetRecordByView[FileCabinetViewKey];

/**
 * What `list` can ask for: a table's list, or `call.all` — every call, whatever its method. No table
 * shows `call.all`; "Attached to" and "Referenced by" ask it once instead of once per call table.
 */
export type FileCabinetListKey = FileCabinetViewKey | 'call.all';

export type FileCabinetRecordByList = FileCabinetRecordByView & { 'call.all': CabinetCall };

/** A record's key. Every record has `id` except an AI call, keyed by `aiPhoneId`. */
export function fileCabinetRecordId(view: FileCabinetViewKey, record: FileCabinetRecord): string | undefined {
    return view === 'call.ai' ? (record as CabinetAiCall).aiPhoneId : (record as Exclude<FileCabinetRecord, CabinetAiCall>).id;
}

/** What one list call asks for. `filters` are wire values, operator included — `EQ:…`, `FTS:…`. */
export interface FileCabinetListRequest {
    limit: number;
    offset: number;
    /** The endpoint's sort enum value, e.g. `CREATED`. */
    sort?: string;
    order?: 'ASCENDING' | 'DESCENDING';
    filters: Record<string, string[]>;
}

/** One page, with the total from `pagination-total`. */
export interface FileCabinetPage<T> {
    items: T[];
    total: number;
}

/** What a source can do, so the screen offers only that — rather than failing after the user asked. */
export interface FileCabinetCapabilities {
    /** `FTS:` search — OCR text, transcripts. */
    fullTextSearch: boolean;
    /** Date and number filters (`GT:`, `LTE:`, `BT:` …). */
    rangeFilters: boolean;
    /**
     * Documents can be inactive: `/support/document` takes `filterActive` (sandbox 2026-10-23: 1,207 = 1,200
     * active + 7 inactive); `/provider/document` ignores it (1,200 every way). Where true, All Documents
     * lists active ones unless the Active filter says otherwise — as the old Documents page does.
     */
    inactiveDocuments: boolean;
}

/**
 * Where the File Cabinet's data comes from: the provider API, the support API, or the provider demo
 * generators. The store never touches a client directly, so the same store runs on all three.
 */
export interface FileCabinetDataSource {
    readonly app: FileCabinetApp;
    /** The values the document-type filter offers — this app's display enum, not the other's. */
    readonly documentTypes: string[];
    readonly capabilities: FileCabinetCapabilities;
    /**
     * One page of a list. The source adds what makes a table what it is — the call method of
     * `call.in`/`out`/`other` (`fileCabinetWireFilters`) — so no caller can get every call by asking
     * for the inbound ones.
     */
    list<L extends FileCabinetListKey>(
        list: L,
        request: FileCabinetListRequest,
    ): Promise<FileCabinetPage<FileCabinetRecordByList[L]>>;
    /** One record by its key — a link to an open drawer. Undefined when there is none with that key. */
    get<V extends FileCabinetViewKey>(view: V, id: string): Promise<FileCabinetRecordByView[V] | undefined>;
    /**
     * The file behind a record. Undefined when the record has no file — a letter cancelled before it
     * was printed, a fax with no document. Both URLs are fetched when the drawer opens: a new tab
     * opened after an `await` loses the click and is blocked as a pop-up.
     */
    fileUrls(view: FileCabinetViewKey, record: FileCabinetRecord): Promise<FileCabinetFileUrls | undefined>;
    /** A call recording's transcript — `GET /document/{id}/transcription`. */
    transcription(documentId: string): Promise<CabinetTranscription | undefined>;
    /** Optional: why a table is empty when this source says so — demo mode has no emails or calls. */
    emptyNote?(view: FileCabinetViewKey): string | undefined;
}

export interface FileCabinetFileUrls {
    /** Preview and Download. Document links are served `Content-Disposition: attachment`. */
    download: string;
    /** Open in New Tab — an inline (`/embed`) link where the API has one, else the same URL. */
    view: string;
}

/**
 * A failed request, with its HTTP status. The status decides what the screen says: 403 is "you
 * cannot see this channel", which is neither "empty" nor "broken" (NS-3). Measured on sandbox
 * 2026-10-22: one support account gets 403 on mail, faxes and calls but 200 on documents.
 */
export class FileCabinetRequestError extends Error {
    public readonly status: number | undefined;
    /**
     * The server's error code (`err.letter_does_not_exist`), or `invalid_id` for a 400 that rejects the
     * record id itself. The API does not answer 404 for a missing record — see `notFound`.
     */
    public readonly code: string | undefined;

    constructor(message: string, status?: number, code?: string) {
        super(message);
        this.name = 'FileCabinetRequestError';
        this.status = status;
        this.code = code;
        Object.setPrototypeOf(this, FileCabinetRequestError.prototype);
    }

    public get forbidden(): boolean {
        return this.status === 403;
    }

    /**
     * There is no record with that id. Measured on sandbox (2026-10-23, GET by a nonexistent id): no
     * endpoint answers 404 — `/document` answers 400 `{errors: {id: "Invalid document id"}}`, every other
     * list 500 with `err.<thing>_does_not_exist` or `err.fax_not_found` (`/email/send` included, its code
     * copied from fax). Any other 500 is a failure, not "not found" (NS-3).
     */
    public get notFound(): boolean {
        return this.status === 404 || this.code === 'invalid_id' || /(_does_not_exist|_not_found)$/.test(this.code ?? '');
    }
}
